import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { format, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import React from 'react';
import useGetShippingAddressHistory from '../../../../../../hooks/orders/useGetShippingAddressHistory';

const FIELD_LABELS = {
  firstName: 'Prénom',
  lastName: 'Nom',
  phone: 'Téléphone',
  line1: 'Adresse',
  line2: "Complément d'adresse",
  postalCode: 'Code postal',
  city: 'Ville',
  country: 'Pays',
};

const SOURCE_LABELS = {
  pandora: 'Pandora',
  admin: 'Admin BO',
};

/**
 * Historique des modifications d'adresse de livraison (audit trail), par ordre
 * chronologique : date, auteur, source et champs modifiés (ancien → nouveau)
 */
const ShippingAddressHistory = ({ orderId }) => {
  const { history, isLoading, error } = useGetShippingAddressHistory(orderId);

  if (isLoading) {
    return <CircularProgress size={24} sx={{ alignSelf: 'center' }} />;
  }

  const renderContent = () => {
    // Échec de chargement sans données déjà connues : ne pas laisser croire
    // à une adresse jamais modifiée (SWR garde les données en cas d'échec
    // d'un rafraîchissement, elles restent alors affichées)
    if (error && history.length === 0) {
      return (
        <Typography variant="body2" color="error">
          Impossible de charger l&apos;historique des modifications.
        </Typography>
      );
    }
    if (history.length === 0) {
      return (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          Aucune modification enregistrée.
        </Typography>
      );
    }
    return history.map((entry, index) => {
      const changedFields = Object.keys(FIELD_LABELS).filter(
        (field) =>
          (entry.oldAddress?.[field] || '') !==
          (entry.newAddress?.[field] || '')
      );
      // une date invalide ferait planter format() pendant le rendu, et avec lui
      // tout le formulaire d'adresse
      const createdAt = new Date(entry.createdAt);
      return (
        <Box key={`${entry.createdAt}-${index}`}>
          {index > 0 && <Divider sx={{ my: 1 }} />}
          <Typography variant="subtitle2">
            {isValid(createdAt)
              ? format(createdAt, 'dd/MM/yyyy HH:mm', { locale: fr })
              : 'Date inconnue'}{' '}
            — {entry.updatedBy} ({SOURCE_LABELS[entry.source] || entry.source})
          </Typography>
          {changedFields.map((field) => (
            <Typography key={field} variant="body2">
              {FIELD_LABELS[field]} :{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                {entry.oldAddress?.[field] || '(vide)'}
              </Box>
              {' → '}
              <Box component="span" sx={{ color: 'success.main' }}>
                {entry.newAddress?.[field] || '(vide)'}
              </Box>
            </Typography>
          ))}
        </Box>
      );
    });
  };

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
        Historique des modifications
      </Typography>
      {renderContent()}
    </Card>
  );
};

export default ShippingAddressHistory;
